import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useEditorAutoFocus } from '@/hooks/useEditorAutoFocus';
import { cn } from '@/lib/utils';
import {
  Bold,
  CheckCheck,
  Code,
  Heading1,
  Heading2,
  Italic,
  List,
  ListOrdered,
  Loader2,
  Quote,
  Save
} from 'lucide-react';
import React, { KeyboardEvent, useCallback } from 'react';
import {
  BaseEditor,
  createEditor,
  Descendant,
  Editor,
  Node,
  Element as SlateElement,
  Transforms
} from 'slate';
import { HistoryEditor, withHistory } from 'slate-history';
import {
  Editable,
  ReactEditor,
  Slate,
  withReact
} from 'slate-react';

// Define element types for Markdown
const ELEMENT_TYPES = {
  PARAGRAPH: 'paragraph',
  HEADING_1: 'heading-1',
  HEADING_2: 'heading-2',
  HEADING_3: 'heading-3',
  BLOCKQUOTE: 'blockquote',
  CODE_BLOCK: 'code-block',
  BULLETED_LIST: 'bulleted-list',
  NUMBERED_LIST: 'numbered-list',
  LIST_ITEM: 'list-item'
} as const;

// Element type for TypeScript
type ElementType = typeof ELEMENT_TYPES[keyof typeof ELEMENT_TYPES];

// Define simple editor types
type CustomElement = {
  type: ElementType;
  children: CustomText[];
};

type CustomText = {
  text: string;
  bold?: boolean;
  italic?: boolean;
  code?: boolean;
};

declare module 'slate' {
  interface CustomTypes {
    Editor: BaseEditor & ReactEditor & HistoryEditor;
    Element: CustomElement;
    Text: CustomText;
  }
}

// Default plate-like value structure
const defaultValue: CustomElement[] = [
  {
    type: ELEMENT_TYPES.PARAGRAPH,
    children: [{ text: '' }],
  },
];

// Helper function to create a default paragraph
const createDefaultParagraph = (text: string = ''): CustomElement => ({
  type: ELEMENT_TYPES.PARAGRAPH,
  children: [{ text }],
});

// Editor props
interface SimpleEditorProps {
  value: Descendant[];
  onChange: (value: Descendant[]) => void;
  readOnly?: boolean;
  onSave?: () => void;
  isSaving?: boolean;
  saveStatus?: 'saved' | 'unsaved' | 'saving';
}

// Supported format types
type FormatType = 'bold' | 'italic' | 'code';

// Minimal tooltip-wrapped icon button for format/block buttons
const ToolbarButton = ({
  isActive,
  onMouseDown,
  title,
  children,
}: {
  isActive: boolean;
  onMouseDown: (e: React.MouseEvent) => void;
  title: string;
  children: React.ReactNode;
}) => (
  <TooltipProvider delayDuration={600}>
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          onMouseDown={onMouseDown}
          className={cn(
            'h-7 w-7 flex items-center justify-center rounded transition-colors text-muted-foreground hover:text-foreground hover:bg-accent',
            isActive && 'bg-accent text-foreground'
          )}
        >
          {children}
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" className="text-xs">
        {title}
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

// Custom formatting buttons for text formatting
const FormatButton = ({
  format,
  icon,
  editor,
  title,
}: {
  format: FormatType;
  icon: React.ReactNode;
  editor: ReactEditor & HistoryEditor & BaseEditor;
  title: string;
}) => {
  const isFormatActive = () => {
    const marks = Editor.marks(editor);
    return marks ? marks[format] === true : false;
  };

  const toggleFormat = (event: React.MouseEvent) => {
    event.preventDefault();
    if (isFormatActive()) {
      Editor.removeMark(editor, format);
    } else {
      Editor.addMark(editor, format, true);
    }
  };

  return (
    <ToolbarButton isActive={isFormatActive()} onMouseDown={toggleFormat} title={title}>
      {icon}
    </ToolbarButton>
  );
};

// Button for block types
const BlockButton = ({
  format,
  icon,
  editor,
  title,
}: {
  format: ElementType;
  icon: React.ReactNode;
  editor: ReactEditor & HistoryEditor & BaseEditor;
  title: string;
}) => {
  const isBlockActive = (format: ElementType) => {
    const { selection } = editor;
    if (!selection) return false;

    const [match] = Array.from(
      Editor.nodes(editor, {
        at: Editor.unhangRange(editor, selection),
        match: n => !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === format,
      })
    );

    return !!match;
  };

  const toggleBlock = (event: React.MouseEvent) => {
    event.preventDefault();

    const isList = format === ELEMENT_TYPES.BULLETED_LIST || format === ELEMENT_TYPES.NUMBERED_LIST;

    Transforms.unwrapNodes(editor, {
      match: n =>
        !Editor.isEditor(n) &&
        SlateElement.isElement(n) &&
        [ELEMENT_TYPES.BULLETED_LIST, ELEMENT_TYPES.NUMBERED_LIST].includes(n.type as any),
      split: true,
    });

    const isActive = isBlockActive(format);
    const newType = isActive ? ELEMENT_TYPES.PARAGRAPH : isList ? ELEMENT_TYPES.LIST_ITEM : format;

    Transforms.setNodes(editor, { type: newType } as Partial<SlateElement>);

    if (!isActive && isList) {
      const block = { type: format, children: [] };
      Transforms.wrapNodes(editor, block as any);
    }
  };

  return (
    <ToolbarButton isActive={isBlockActive(format)} onMouseDown={toggleBlock} title={title}>
      {icon}
    </ToolbarButton>
  );
};

// Simple Plate Editor component
export const SimplePlateEditor: React.FC<SimpleEditorProps> = ({
  value,
  onChange,
  readOnly = false,
  onSave,
  isSaving = false,
  saveStatus = 'saved'
}) => {
  // Initialize the editor object
  const [editor] = React.useState(() => {
    const newEditor = withHistory(withReact(createEditor()));
    return newEditor;
  });

  // Enable autofocus when user switches back to tab (but not on mount for this simpler editor)
  useEditorAutoFocus(editor, {
    focusOnMount: false,
    focusOnWindowFocus: false,
    delay: 100
  });

  // Initialize editor content only once when the component mounts
  const [isInitialized, setIsInitialized] = React.useState(false);

  // Track when we're programmatically updating to prevent onChange loops
  const isProgrammaticUpdate = React.useRef(false);

  // Optimize content synchronization
  React.useEffect(() => {
    if (!editor) return;

    if (!isInitialized) {
      try {
        const normalizedValue = normalizeValue(value);
        isProgrammaticUpdate.current = true;
        editor.children = normalizedValue;
        setIsInitialized(true);
        editor.onChange();
        isProgrammaticUpdate.current = false;
      } catch (error) {
        console.error('Error initializing editor content:', error);
        editor.children = defaultValue;
        setIsInitialized(true);
        editor.onChange();
      }
      return;
    }

    // After initialization, if the value prop changes from an external source
    // (different reference than what's currently in the editor), update the editor.
    if (value && value !== editor.children) {
      // We only update if it's a different reference, which indicates an external change
      // like data finally loading from the server.
      try {
        const normalizedValue = normalizeValue(value);
        isProgrammaticUpdate.current = true;
        editor.children = normalizedValue;
        editor.onChange();
        isProgrammaticUpdate.current = false;
      } catch (error) {
        console.error('Error updating editor content from prop:', error);
      }
    }
  }, [editor, value, isInitialized]);

  // Wrapped onChange handler that respects programmatic updates
  const handleChange = useCallback((value: Descendant[]) => {
    if (!isProgrammaticUpdate.current) {
      onChange(value);
    }
  }, [onChange]);

  // Define leaf rendering for inline formats
  const renderLeaf = useCallback((props: any) => {
    let { attributes, children, leaf } = props;

    if (leaf.bold) {
      children = <strong>{children}</strong>;
    }

    if (leaf.italic) {
      children = <em>{children}</em>;
    }

    if (leaf.code) {
      children = <code className="bg-muted px-1 py-0.5 rounded text-sm font-mono">{children}</code>;
    }

    return <span {...attributes}>{children}</span>;
  }, []);

  // Define element rendering for block formats
  const renderElement = useCallback((props: any) => {
    const { attributes, children, element } = props;

    switch (element.type) {
      case ELEMENT_TYPES.HEADING_1:
        return <h1 className="text-2xl font-bold my-3 text-foreground" {...attributes}>{children}</h1>;
      case ELEMENT_TYPES.HEADING_2:
        return <h2 className="text-xl font-semibold my-2.5 text-foreground" {...attributes}>{children}</h2>;
      case ELEMENT_TYPES.HEADING_3:
        return <h3 className="text-lg font-semibold my-2 text-foreground" {...attributes}>{children}</h3>;
      case ELEMENT_TYPES.BLOCKQUOTE:
        return (
          <blockquote
            className="border-l-2 border-border pl-4 my-3 text-muted-foreground italic"
            {...attributes}
          >
            {children}
          </blockquote>
        );
      case ELEMENT_TYPES.CODE_BLOCK:
        return (
          <pre className="bg-muted/60 rounded-lg p-4 my-3 font-mono text-sm overflow-x-auto" {...attributes}>
            <code>{children}</code>
          </pre>
        );
      case ELEMENT_TYPES.BULLETED_LIST:
        return <ul className="list-disc ml-5 my-2 space-y-1" {...attributes}>{children}</ul>;
      case ELEMENT_TYPES.NUMBERED_LIST:
        return <ol className="list-decimal ml-5 my-2 space-y-1" {...attributes}>{children}</ol>;
      case ELEMENT_TYPES.LIST_ITEM:
        return <li className="leading-relaxed" {...attributes}>{children}</li>;
      default:
        return <p className="my-1 leading-relaxed" {...attributes}>{children}</p>;
    }
  }, []);

  // Convert our custom value to a format Slate can use
  const normalizeValue = (val: any): Descendant[] => {
    if (!val) return defaultValue;

    if (typeof val === 'string') {
      try {
        const parsed = JSON.parse(val);
        return normalizeValue(parsed);
      } catch (e) {
        return defaultValue;
      }
    }

    if (!Array.isArray(val)) return defaultValue;
    if (val.length === 0) return defaultValue;

    try {
      return val.map(node => {
        if (!node) return createDefaultParagraph();
        if (!node.children) return createDefaultParagraph();

        const children = Array.isArray(node.children)
          ? node.children.map((child: any) => {
            if (!child) return { text: '' };
            if (typeof child !== 'object') return { text: '' };
            if (typeof child.text !== 'string') return { text: '' };
            return child;
          })
          : [{ text: '' }];

        let type = node.type || ELEMENT_TYPES.PARAGRAPH;

        if (type === 'h1') type = ELEMENT_TYPES.HEADING_1;
        if (type === 'h2') type = ELEMENT_TYPES.HEADING_2;
        if (type === 'h3') type = ELEMENT_TYPES.HEADING_3;
        if (type === 'blockquote') type = ELEMENT_TYPES.BLOCKQUOTE;
        if (type === 'code') type = ELEMENT_TYPES.CODE_BLOCK;
        if (type === 'ul') type = ELEMENT_TYPES.BULLETED_LIST;
        if (type === 'ol') type = ELEMENT_TYPES.NUMBERED_LIST;
        if (type === 'li') type = ELEMENT_TYPES.LIST_ITEM;
        if (type === 'p') type = ELEMENT_TYPES.PARAGRAPH;

        return { type, children };
      });
    } catch (error) {
      return defaultValue;
    }
  };

  // Handle keyboard shortcuts for markdown-like formatting
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!readOnly) {
      if (event.ctrlKey && event.key === 'b') {
        event.preventDefault();
        Editor.addMark(editor, 'bold', true);
      } else if (event.ctrlKey && event.key === 'i') {
        event.preventDefault();
        Editor.addMark(editor, 'italic', true);
      } else if (event.ctrlKey && event.key === '`') {
        event.preventDefault();
        Editor.addMark(editor, 'code', true);
      } else if (event.ctrlKey && event.key === 's') {
        event.preventDefault();
        onSave?.();
      }

      if (event.key === ' ') {
        const { selection } = editor;
        if (selection && selection.focus.offset > 0) {
          const [node] = Editor.node(editor, selection.focus.path);
          const start = Editor.start(editor, selection.focus.path);
          const range = { anchor: start, focus: selection.focus };
          const text = Node.string(node);
          const beforeText = text.slice(0, selection.focus.offset);

          let type: ElementType | null = null;

          if (beforeText === '#') {
            type = ELEMENT_TYPES.HEADING_1;
          } else if (beforeText === '##') {
            type = ELEMENT_TYPES.HEADING_2;
          } else if (beforeText === '###') {
            type = ELEMENT_TYPES.HEADING_3;
          } else if (beforeText === '>') {
            type = ELEMENT_TYPES.BLOCKQUOTE;
          } else if (beforeText === '```') {
            type = ELEMENT_TYPES.CODE_BLOCK;
          } else if (beforeText === '-' || beforeText === '*') {
            type = ELEMENT_TYPES.LIST_ITEM;

            event.preventDefault();
            Transforms.delete(editor, { at: range });
            Transforms.setNodes(editor, { type });

            const [listMatch] = Array.from(
              Editor.nodes(editor, {
                match: n => !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === ELEMENT_TYPES.BULLETED_LIST,
              })
            );

            if (!listMatch) {
              Transforms.wrapNodes(editor, { type: ELEMENT_TYPES.BULLETED_LIST, children: [] } as any);
            }
            return;
          } else if (beforeText === '1.') {
            type = ELEMENT_TYPES.LIST_ITEM;

            event.preventDefault();
            Transforms.delete(editor, { at: range });
            Transforms.setNodes(editor, { type });

            const [listMatch] = Array.from(
              Editor.nodes(editor, {
                match: n => !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === ELEMENT_TYPES.NUMBERED_LIST,
              })
            );

            if (!listMatch) {
              Transforms.wrapNodes(editor, { type: ELEMENT_TYPES.NUMBERED_LIST, children: [] } as any);
            }
            return;
          }

          if (type) {
            event.preventDefault();
            Transforms.delete(editor, { at: range });
            Transforms.setNodes(editor, { type } as any);
          }
        }
      }
    }
  };

  const SaveStatusIcon = () => {
    if (saveStatus === 'saving') return <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />;
    if (saveStatus === 'saved') return <CheckCheck className="h-3 w-3 text-emerald-500" />;
    return <div className="h-1.5 w-1.5 rounded-full bg-amber-400 mt-px" />;
  };

  const saveStatusLabel = saveStatus === 'saved' ? 'Saved' : saveStatus === 'saving' ? 'Saving…' : 'Unsaved';

  return (
    <div className="relative flex flex-col">
      {/* Toolbar — above the writing area */}
      {!readOnly && (
        <div className="flex items-center gap-0.5 mb-3 pb-3 border-b border-border/50">
          {/* Text formatting */}
          <FormatButton
            format="bold"
            icon={<Bold className="h-3.5 w-3.5" />}
            editor={editor}
            title="Bold (Ctrl+B)"
          />
          <FormatButton
            format="italic"
            icon={<Italic className="h-3.5 w-3.5" />}
            editor={editor}
            title="Italic (Ctrl+I)"
          />
          <FormatButton
            format="code"
            icon={<Code className="h-3.5 w-3.5" />}
            editor={editor}
            title="Inline code (Ctrl+`)"
          />

          <div className="w-px h-4 bg-border/60 mx-1" />

          {/* Block formatting */}
          <BlockButton
            format={ELEMENT_TYPES.HEADING_1}
            icon={<Heading1 className="h-3.5 w-3.5" />}
            editor={editor}
            title="Heading 1 (# + space)"
          />
          <BlockButton
            format={ELEMENT_TYPES.HEADING_2}
            icon={<Heading2 className="h-3.5 w-3.5" />}
            editor={editor}
            title="Heading 2 (## + space)"
          />
          <BlockButton
            format={ELEMENT_TYPES.BLOCKQUOTE}
            icon={<Quote className="h-3.5 w-3.5" />}
            editor={editor}
            title="Blockquote (> + space)"
          />
          <BlockButton
            format={ELEMENT_TYPES.CODE_BLOCK}
            icon={<Code className="h-3.5 w-3.5" />}
            editor={editor}
            title="Code block (``` + space)"
          />

          <div className="w-px h-4 bg-border/60 mx-1" />

          {/* List formatting */}
          <BlockButton
            format={ELEMENT_TYPES.BULLETED_LIST}
            icon={<List className="h-3.5 w-3.5" />}
            editor={editor}
            title="Bulleted list (- + space)"
          />
          <BlockButton
            format={ELEMENT_TYPES.NUMBERED_LIST}
            icon={<ListOrdered className="h-3.5 w-3.5" />}
            editor={editor}
            title="Numbered list (1. + space)"
          />

          {/* Save + status */}
          {onSave && (
            <>
              <div className="w-px h-4 bg-border/60 mx-1" />
              <button
                onClick={onSave}
                disabled={isSaving || saveStatus === 'saved'}
                className={cn(
                  'flex items-center gap-1.5 h-7 px-2.5 rounded-lg text-xs font-medium transition-colors',
                  saveStatus === 'saved'
                    ? 'text-muted-foreground cursor-default'
                    : 'text-foreground hover:bg-accent cursor-pointer'
                )}
              >
                {saveStatus !== 'saved' && <Save className="h-3.5 w-3.5" />}
                <SaveStatusIcon />
                <span>{saveStatusLabel}</span>
              </button>
            </>
          )}
        </div>
      )}

      {/* Writing area */}
      <Slate
        editor={editor}
        initialValue={defaultValue}
        onChange={handleChange}
      >
        <Editable
          readOnly={readOnly}
          renderLeaf={renderLeaf}
          renderElement={renderElement}
          placeholder={!readOnly ? "Start writing… (supports Markdown shortcuts like # ## > - 1.)" : ""}
          style={{ minHeight: '480px', padding: '2px' }}
          className="focus:outline-none text-sm text-foreground leading-relaxed"
          onKeyDown={handleKeyDown}
        />
      </Slate>
    </div>
  );
};

export default SimplePlateEditor;